import React, { useState, useEffect } from "react";
import axios from "axios";
export { default as Certification } from "./Certification";

// Production API URL pointing directly to your backend folder
const API = "/backend/send-enquiry.php";

export const SOCIAL = {
  whatsapp: "https://wa.me/971525131571",
  instagram: "#",
  facebook: "#",
  linkedin: "#",
  twitter: "#",
  youtube: "#",
  email: "#",
  phone: "tel:+97165526501",
};

export function FloatingActions() {
  return (
    <div className="bj-fab" data-testid="floating-actions">
      <a
        href={SOCIAL.whatsapp}
        target="_blank"
        rel="noreferrer"
        style={{ background: "#25D366" }}
        data-testid="fab-whatsapp"
        aria-label="WhatsApp"
      >
        <i className="fab fa-whatsapp" />
      </a>

      <a
        href={SOCIAL.phone}
        style={{ background: "var(--bj-blue-600)" }}
        data-testid="fab-phone"
        aria-label="Call"
      >
        <i className="fas fa-phone" />
      </a>
    </div>
  );
}

export function SocialRow({ light = false }) {
  const cls = light
    ? "text-white/80 hover:text-white"
    : "text-blue-900 hover:text-blue-700";

  return (
    <div className="flex items-center gap-3" data-testid="social-row">
      {[
        ["facebook", SOCIAL.facebook],
        ["instagram", SOCIAL.instagram],
        ["linkedin-in", SOCIAL.linkedin],
        ["twitter", SOCIAL.twitter],
        ["whatsapp", SOCIAL.whatsapp],
        ["youtube", SOCIAL.youtube],
      ].map(([icon, href]) => {
        const isWhatsapp = icon === "whatsapp";

        return isWhatsapp ? (
          <a
            key={icon}
            href={href}
            target="_blank"
            rel="noreferrer"
            className={`w-9 h-9 rounded-full border ${
              light ? "border-white/30" : "border-blue-200"
            } flex items-center justify-center transition-all hover:-translate-y-0.5 hover:shadow-lg ${cls}`}
            data-testid={`social-${icon}`}
            aria-label={icon}
          >
            <i className={`fab fa-${icon}`} />
          </a>
        ) : (
          <div
            key={icon}
            className={`w-9 h-9 rounded-full border ${
              light ? "border-white/30" : "border-blue-200"
            } flex items-center justify-center opacity-70 cursor-default ${cls}`}
            data-testid={`social-${icon}`}
            aria-label={icon}
          >
            <i className={`fab fa-${icon}`} />
          </div>
        );
      })}
    </div>
  );
}

export function ContactForm({
  division = "general",
  services = [],
  accentClass = "bg-blue-700",
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });

  const [status, setStatus] = useState({
    state: "idle",
    msg: "",
  });

  const set = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.value,
    });

  const submit = async (e) => {
    e.preventDefault();

    setStatus({
      state: "loading",
      msg: "Sending your enquiry...",
    });

    try {
      const response = await axios.post(
        API,
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          service: form.service || services[0] || "General Inquiry",
          message: form.message,
          division: division,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("API RESPONSE:", response.data);

      setStatus({
        state: "success",
        msg:
          response.data?.message ||
          "Thank you! Your enquiry has been submitted successfully. Please check your email for confirmation.",
      });

      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
      });
    } catch (err) {
      console.error("CONTACT FORM ERROR:", err);

      setStatus({
        state: "error",
        msg:
          err.response?.data?.message ||
          err.response?.data?.detail ||
          "Unable to send enquiry. Please try again later.",
      });
    }
  };

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid={`contact-form-${division}`}
    >
      <input
        required
        value={form.name}
        onChange={set("name")}
        placeholder="Full Name *"
        className="px-4 py-3 rounded-xl border border-blue-100 focus:border-blue-600 outline-none bg-white"
        data-testid="input-name"
      />

      <input
        required
        type="email"
        value={form.email}
        onChange={set("email")}
        placeholder="Email *"
        className="px-4 py-3 rounded-xl border border-blue-100 focus:border-blue-600 outline-none bg-white"
        data-testid="input-email"
      />

      <input
        value={form.phone}
        onChange={set("phone")}
        placeholder="Phone"
        className="px-4 py-3 rounded-xl border border-blue-100 focus:border-blue-600 outline-none bg-white"
        data-testid="input-phone"
      />

      <input
        value={form.company}
        onChange={set("company")}
        placeholder="Company"
        className="px-4 py-3 rounded-xl border border-blue-100 focus:border-blue-600 outline-none bg-white"
        data-testid="input-company"
      />

      {services.length > 0 && (
        <select
          value={form.service}
          onChange={set("service")}
          className="md:col-span-2 px-4 py-3 rounded-xl border border-blue-100 focus:border-blue-600 outline-none bg-white"
          data-testid="input-service"
        >
          <option value="">Select a service</option>
          {services.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      )}

      <textarea
        required
        rows={5}
        value={form.message}
        onChange={set("message")}
        placeholder="Your message / requirement *"
        className="md:col-span-2 px-4 py-3 rounded-xl border border-blue-100 focus:border-blue-600 outline-none bg-white resize-none"
        data-testid="input-message"
      />

      <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
        <p
          className={`text-sm ${
            status.state === "error"
              ? "text-red-600"
              : status.state === "success"
              ? "text-emerald-700"
              : "text-slate-500"
          }`}
          data-testid="form-status"
        >
          {status.msg}
        </p>

        <button
          disabled={status.state === "loading"}
          className={`bj-btn bj-btn-primary bj-shimmer ${accentClass}`}
          type="submit"
          data-testid="submit-contact"
        >
          <i className="fas fa-paper-plane mr-2" />
          {status.state === "loading" ? "Sending..." : "Send Enquiry"}
        </button>
      </div>
    </form>
  );
}

export function TeamSection() {
  const teamMembers = [
    {
      name: "Zubair Bin Mohammed Bajabar",
      role: "Managing Director",
      email: "zubair@bajabarinc.ae",
      image: "/team-member1.png",
    },
    {
      name: "Muhammad Ahsan",
      role: "Operation And Admin Head",
      email: "sales@bajabarinc.ae",
      image: "/team-member2.png",
    },
    {
      name: "Maz Pasha",
      role: "Assistant Operations Manager",
      email: "sales@bajabarinc.ae",
      image: "/team-member3.png",
    },
    {
      name: "Ghouse Chida",
      role: "Accounts Manager",
      email: "accounts@bajabarinc.ae",
      image: "/team-member4.png",
    },
  ];

  return (
    <div className="pt-16 border-t border-slate-100" id="team">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <span className="inline-flex px-5 py-2 rounded-full bg-blue-50 text-[#0A56C6] font-bold tracking-[0.2em] uppercase text-xs md:text-sm">
          Our Leadership
        </span>
        <h3 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-[-1px]">
          Meet Our Logistics{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0A56C6] to-cyan-500">
            Experts
          </span>
        </h3>
        <p className="mt-3 text-slate-600 text-sm md:text-base">
          Dedicated professionals bringing years of industry expertise to deliver reliable freight forwarding and support.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
        {teamMembers.map((member) => (
          <div
            key={member.name}
            className="group bg-slate-50 hover:bg-white rounded-[32px] overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 flex flex-col justify-between"
          >
            <div className="relative w-full h-80 bg-slate-200 overflow-hidden">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>

            <div className="p-6 md:p-7 flex-grow flex flex-col justify-between">
              <div>
                <h4 className="text-xl font-bold text-slate-900 leading-snug">
                  {member.name}
                </h4>
                <p className="mt-1.5 text-sm font-semibold text-[#0A56C6]">
                  {member.role}
                </p>
                <p className="mt-3 text-xs font-medium text-slate-500 break-all">
                  {member.email}
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-200/60 flex items-center justify-end gap-3">
                <a
                  href={`mailto:${member.email}`}
                  title={`Email ${member.name}`}
                  className="w-11 h-11 rounded-2xl bg-white border border-slate-200 text-slate-600 flex items-center justify-center transition-all duration-300 hover:bg-[#0A56C6] hover:text-white hover:border-[#0A56C6] hover:-translate-y-1 hover:shadow-md"
                >
                  <i className="fas fa-envelope text-base" />
                </a>

                <a
                  href="#"
                  title="LinkedIn Profile"
                  className="w-11 h-11 rounded-2xl bg-white border border-slate-200 text-slate-600 flex items-center justify-center transition-all duration-300 hover:bg-[#0A56C6] hover:text-white hover:border-[#0A56C6] hover:-translate-y-1 hover:shadow-md"
                >
                  <i className="fab fa-linkedin-in text-base" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Footer({ division = "trading" }) {
  return (
    <footer className="relative mt-20" data-testid="site-footer">
      <div className="bj-mesh text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
          {/* COMPANY */}
          <div className="md:col-span-2">
            <div className="font-display text-3xl font-bold">
              BAJABAR
              <span className="text-blue-200"> INCORPORATION FZE</span>
            </div>

            <p className="text-blue-100/80 mt-4 max-w-md">
              Your trusted partner in UAE for Swift Freight Handling and a premier
              Supply House for Electrical, Lighting, Telecom and Power.
            </p>

            <div className="mt-6">
              <SocialRow light />
            </div>
          </div>

          {/* UAE OFFICE */}
          <div>
            <h4 className="font-semibold mb-3 text-white">
              Head Office — UAE
            </h4>

            <p className="text-blue-100/80 text-sm leading-relaxed">
              Building Z2, Off. #52, SAIF Zone
              <br />
              P.O. Box: 513813, Sharjah, UAE
              <br />
              +971 6 5526501 / 06 5384330
              <br />
              sales@bajabarinc.ae
            </p>
          </div>

          {/* INDIA OFFICE */}
          <div>
            <h4 className="font-semibold mb-3 text-white">
              Backend Office
            </h4>

            <p className="text-blue-100/80 text-sm leading-relaxed">
              Bajabar Tower, Macca Colony,
              <br />
              Tadban, Hyderabad, Telangana
              <br />
              +91 79951 73571
              <br />
              accounts-ind@bajabarinc.ae
            </p>
          </div>
        </div>

        {/* FOOTER BOTTOM */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between text-sm text-blue-100/70">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4">
              <span>
                © 2026 BAJABAR INCORPORATION FZE. All rights reserved.
              </span>

              <span className="text-blue-100/60">
                Design & Developed by{" "}
                <a
                  href="https://crescentitsolutions.com"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white transition-colors duration-300 font-medium"
                >
                  Crescent IT Solutions INC IL, USA
                </a>
              </span>
            </div>

            <a href="/" className="bj-link" data-testid="footer-home-link">
              Back to home
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function Navbar({
  active = "home",
  brandTo = "/",
  showProducts = false,
  showVertiv = false,
  showBrands = false,
}) {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    {
      label: "Home",
      href: "#home",
    },
    {
      label: "About",
      href: "#about",
    },
    ...(showProducts
      ? [
          {
            label: "Products",
            href: "#products",
          },
        ]
      : []),
    ...(showVertiv
      ? [
          {
            label: "Vertiv",
            href: "#vertiv",
          },
        ]
      : []),
    ...(showBrands
      ? [
          {
            label: "Brands",
            href: "#brands",
          },
        ]
      : []),
    {
      label: "Why Us",
      href: "#why",
    },
    {
      label: "Contact",
      href: "#contact",
    },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-2xl shadow-xl border-b border-blue-100"
          : "bg-white/70 backdrop-blur-xl"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* LOGO */}
        <a
          href={brandTo}
          className="flex items-center group"
          data-testid="brand-link"
        >
          <img
            src="/logo.png"
            alt="BAJABAR Logo"
            className="h-20 sm:h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-2xl hover:scale-105 transition-all duration-500"
          />
        </a>

        {/* DESKTOP MENU */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="relative text-[15px] font-semibold text-slate-700 hover:text-blue-700 transition-all duration-300 group"
            >
              {item.label}
              <span className="absolute left-0 -bottom-2 w-0 h-[2px] bg-blue-700 transition-all duration-300 group-hover:w-full"></span>
            </a>
          ))}
        </nav>

        {/* RIGHT SECTION */}
        <div className="flex items-center gap-4">
          {/* CTA BUTTON */}
          <a
            href="#contact"
            className="hidden md:inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 text-white font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-500"
            data-testid="nav-cta"
          >
            Get a Quote
            <i className="fas fa-arrow-right text-sm" />
          </a>

          {/* MOBILE BUTTON */}
          <button
            onClick={() => setMobileMenu(!mobileMenu)}
            className="lg:hidden w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"
          >
            <i
              className={`fas ${
                mobileMenu ? "fa-times" : "fa-bars"
              }`}
            />
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          mobileMenu ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 pb-6 pt-2 bg-white/95 backdrop-blur-xl border-t border-blue-100 flex flex-col gap-5">
          {navLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMobileMenu(false)}
              className="text-slate-700 hover:text-blue-700 font-semibold transition-all duration-300"
            >
              {item.label}
            </a>
          ))}

          <a
            href="#contact"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-bold shadow-lg"
          >
            Get a Quote
            <i className="fas fa-arrow-right text-sm" />
          </a>
        </div>
      </div>
    </header>
  );
}